// src/client/components/EntryQR.tsx
import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { api } from "../lib/api";

type EntryQRProps = {
  hidden?: boolean;
  eventId?: number | null;
  onClose?: () => void;
  onBack?: () => void;
  onHome?: () => void;
};

export default function EntryQR({ hidden, eventId, onClose, onBack, onHome }: EntryQRProps) {
  if (hidden) {
    return null;
  }
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [qrUrl, setQrUrl] = useState<string | null>(null);

  // мини-тост «Скопировано»
  const [toast, setToast] = useState<string | null>(null);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 1400);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setErr(null);

        const r = await api.entry.issue(eventId ?? undefined); // { token: "ENTRY_...", expires_at: ISO }
        if (!alive) return;

        setToken(r.token);
        setExpiresAt(r.expires_at || null);

        // генерируем картинку QR (без зависимостей от canvas/DPR)
        const url = await QRCode.toDataURL(r.token, {
          errorCorrectionLevel: "M",
          margin: 2,
          scale: 8, // 8 → ~256px
          color: { dark: "#000000", light: "#ffffff" },
        });
        if (alive) setQrUrl(url);
      } catch (e: any) {
        setErr(e?.message || "Не удалось выпустить QR");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [eventId]);

  async function copy() {
    if (!token) return;
    try {
      await navigator.clipboard.writeText(token);
      setToast("Скопировано");
    } catch {
      // fallback
      const ta = document.createElement("textarea");
      ta.value = token;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        setToast("Скопировано");
      } finally {
        document.body.removeChild(ta);
      }
    }
  }

  return (
    <div className="grid gap-4">
      <div className="text-sm text-white/80">
        Ваш одноразовый QR действует до конца текущих суток.
      </div>

      {loading && <div className="text-sm text-neutral-300">Готовим QR…</div>}
      {err && (
        <div className="rounded-xl border border-red-400/30 bg-red-500/10 p-3 text-sm text-red-200">
          {err}
        </div>
      )}

      {!loading && !err && (
        <div className="grid place-items-center gap-3">
          <div className="rounded-2xl bg-white p-3">
            {qrUrl ? (
              <img
                src={qrUrl}
                alt="QR для входа"
                width={256}
                height={256}
                style={{ imageRendering: "pixelated", display: "block" }}
              />
            ) : (
              <div className="w-[256px] h-[256px] bg-white" />
            )}
          </div>

          {/* Текстовый токен на всякий случай */}
          {/* <div className="w-full rounded-xl bg-white/5 px-3 py-2 text-xs font-mono tracking-wide text-white/90 overflow-x-auto">
            {token}
          </div> */}

          <div className="text-xs text-white/60">
            Истекает: {expiresAt ? new Date(expiresAt).toLocaleString() : "сегодня"}
          </div>

          {/* <div className="flex gap-2">
            <button
              onClick={copy}
              className="rounded-2xl bg-white/10 hover:bg-white/15 px-4 py-2 text-white"
            >
              Скопировать код
            </button>
            {onClose && (
              <button
                onClick={onClose}
                className="rounded-2xl bg-white/10 hover:bg-white/15 px-4 py-2 text-white"
              >
                Закрыть
              </button>
            )}
          </div> */}
        </div>
      )}

      {/* мини-тост */}
      {toast && (
        <div className="pointer-events-none fixed inset-x-0 bottom-4 flex justify-center">
          <div className="rounded-xl bg-black/70 px-3 py-1.5 text-xs text-white shadow-lg">
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}
